import { MultiplayerService, RoomListEntry } from '../services/MultiplayerService';
import { AuthService } from '../services/AuthService';

export class MultiplayerLobby {
  private container: HTMLElement;
  private multiplayerService: MultiplayerService;
  private authService: AuthService;
  private onRoomJoin: (roomId: string) => void;
  private refreshInterval: number | null = null;

  constructor(
    container: HTMLElement,
    authService: AuthService,
    onRoomJoin: (roomId: string) => void
  ) {
    this.container = container;
    this.authService = authService;
    this.multiplayerService = new MultiplayerService(authService);
    this.onRoomJoin = onRoomJoin;
    
    this.init();
  }

  private init(): void {
    this.render();
    this.startAutoRefresh();
  }

  private render(): void {
    this.container.innerHTML = `
      <div class="multiplayer-lobby">
        <div class="lobby-header">
          <h2>マルチプレイヤーロビー</h2>
          <div class="lobby-actions">
            <button id="create-room-btn" class="btn-primary">
              <span class="icon">➕</span> 新しいルームを作成
            </button>
            <button id="refresh-btn" class="btn-secondary">
              <span class="icon">🔄</span> 更新
            </button>
          </div>
        </div>
        
        <div class="room-list-container">
          <div class="room-list-header">
            <span>ホスト</span>
            <span>プレイヤー</span>
            <span>状態</span>
            <span>アクション</span>
          </div>
          <div id="room-list" class="room-list">
            <div class="loading">ルーム一覧を読み込み中...</div>
          </div>
        </div>
        
        <div class="lobby-info">
          <p>💡 最大4人まで同時にプレイできます</p>
          <p>💡 同じ盤面で早解きを競います</p>
        </div>
      </div>
    `;
    
    this.attachEventListeners();
    this.loadRoomList();
  }

  private attachEventListeners(): void {
    const createBtn = document.getElementById('create-room-btn');
    const refreshBtn = document.getElementById('refresh-btn');
    
    createBtn?.addEventListener('click', () => this.createRoom());
    refreshBtn?.addEventListener('click', () => this.loadRoomList());
  }

  private async createRoom(): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      this.showLoginPrompt();
      return;
    }
    
    try {
      const createBtn = document.getElementById('create-room-btn') as HTMLButtonElement;
      createBtn.disabled = true;
      createBtn.textContent = 'ルームを作成中...';
      
      const roomId = await this.multiplayerService.createRoom(4);
      this.onRoomJoin(roomId);
    } catch (error) {
      console.error('Failed to create room:', error);
      alert('ルームの作成に失敗しました');
      this.render();
    }
  }

  private async joinRoom(roomId: string): Promise<void> {
    const user = this.authService.getCurrentUser();
    if (!user) {
      this.showLoginPrompt();
      return;
    }
    
    try {
      await this.multiplayerService.joinRoom(roomId);
      this.onRoomJoin(roomId);
    } catch (error: any) {
      console.error('Failed to join room:', error);
      alert(error.message || 'ルームへの参加に失敗しました');
    }
  }

  private async loadRoomList(): Promise<void> {
    const listContainer = document.getElementById('room-list');
    if (!listContainer) return;
    
    try {
      const rooms = await this.multiplayerService.getRoomList();
      
      if (rooms.length === 0) {
        listContainer.innerHTML = `
          <div class="no-rooms">
            <p>現在参加可能なルームはありません</p>
            <p class="hint">新しいルームを作成してゲームを始めましょう！</p>
          </div>
        `;
      } else {
        listContainer.innerHTML = rooms.map(room => this.renderRoomItem(room)).join('');
        
        // 参加ボタンのイベントリスナー
        rooms.forEach(room => {
          const joinBtn = document.getElementById(`join-${room.id}`);
          joinBtn?.addEventListener('click', () => this.joinRoom(room.id));
        });
      }
    } catch (error) {
      console.error('Failed to load room list:', error);
      listContainer.innerHTML = `
        <div class="error">
          ルーム一覧の取得に失敗しました
        </div>
      `;
    }
  }

  private renderRoomItem(room: RoomListEntry): string {
    const statusText = room.status === 'waiting' ? '待機中' : '開始準備中';
    const statusClass = room.status === 'waiting' ? 'status-waiting' : 'status-starting';
    const canJoin = room.status === 'waiting' && room.playerCount < room.maxPlayers;
    
    return `
      <div class="room-item">
        <div class="room-host">${this.escapeHtml(room.hostName)}</div>
        <div class="room-players">
          <span class="player-count">${room.playerCount}/${room.maxPlayers}</span>
        </div>
        <div class="room-status ${statusClass}">${statusText}</div>
        <div class="room-action">
          ${canJoin ? 
            `<button id="join-${room.id}" class="btn-join">参加</button>` :
            `<span class="room-full">満員</span>`
          }
        </div>
      </div>
    `;
  }

  private showLoginPrompt(): void {
    const confirmed = confirm('マルチプレイヤーに参加するにはログインが必要です。\nログイン画面に移動しますか？');
    if (confirmed) {
      // ログイン画面を表示するイベントを発行
      window.dispatchEvent(new CustomEvent('show-login'));
    }
  }

  private startAutoRefresh(): void {
    // 5秒ごとに自動更新
    this.refreshInterval = window.setInterval(() => {
      this.loadRoomList();
    }, 5000);
  }

  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  public destroy(): void {
    if (this.refreshInterval !== null) {
      clearInterval(this.refreshInterval);
      this.refreshInterval = null;
    }
  }
}